/**
 * Wizard Types
 * Type definitions for the plan wizard
 */

import { z } from 'zod';
import type { DbIndexation } from './projection';

/** Preprocess for optional number inputs: coerce NaN/empty-string/null to undefined. */
const optionalNumber = (schema: z.ZodNumber) =>
  z.preprocess(
    (v) => (v === '' || v === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v),
    schema.optional(),
  );

// Step 1: Plan Basics
export interface Step1FormData {
  planName: string;
  planType: 'SINGLE' | 'JOINT';
  planAsOfDate: string;
}

// Step 2: People
export interface PersonData {
  fullName: string;
  dateOfBirth: string;
  retirementDate: string;
  statePensionAge: number;
  currentStatePensionAnnual: number;
  taxFreeLumpSumPct: number;
  taxFreeLumpSumCap: number;
  lsaUsedPrePlan?: number;
  /** ISA allowance already used this UK tax year before planAsOfDate — rebaseline only (#1421). */
  isaAllowanceUsedThisTaxYear?: number;
  /**
   * Non-savings income already RECEIVED this UK tax year before the plan starts —
   * salary, SIPP drawdown, State Pension, rental. An actual amount, not a rate.
   * Dividends are NOT included here (see priorDividendIncome) — dividends need
   * distinct tax treatment (the £500 dividend nil-rate band) that this field's
   * ordinary-income banding doesn't apply. The wizard collects salary/drawdown/State
   * Pension/rental as four separate inputs (see priorSalaryIncome etc.) and sums them
   * into this field before submission — the engine itself only takes the total, since
   * all four are taxed identically.
   */
  priorOtherTaxableIncome?: number;
  /**
   * Dividend income already received this UK tax year before the plan starts. Routed
   * through the £500 dividend nil-rate allowance rather than stacked with
   * priorOtherTaxableIncome — see priorOtherTaxableIncome's comment for why the split
   * exists.
   */
  priorDividendIncome?: number;
  /**
   * Wizard-only breakdown input — see priorDrawdownIncome. Salary already received
   * this UK tax year before the plan starts. Summed with priorDrawdownIncome,
   * priorStatePensionIncome and priorRentalIncome into priorOtherTaxableIncome before
   * submission — the engine never sees this field directly (only the sum), so it
   * isn't in the projection request schema.
   */
  priorSalaryIncome?: number;
  /**
   * Wizard-only breakdown input (Task C, 2026-08-19 SDD phase 2) — private pension income (drawdown, annuity, DB)
   * already received this UK tax year before the plan starts. Summed with
   * priorSalaryIncome, priorStatePensionIncome and priorRentalIncome into
   * priorOtherTaxableIncome before submission; the engine never sees this field
   * directly (only the sum), so it isn't in the projection request schema.
   */
  priorDrawdownIncome?: number;
  /** Wizard-only breakdown input — see priorDrawdownIncome. State Pension already received this UK tax year before the plan starts. */
  priorStatePensionIncome?: number;
  /** Wizard-only breakdown input — see priorDrawdownIncome. Rental income already received this UK tax year before the plan starts. */
  priorRentalIncome?: number;
  /** Gross pension contributions already made this UK tax year before the plan starts. */
  priorPensionContributions?: number;
}

export interface Step2FormData {
  persons: PersonData[];
}

// Step 3: Holdings
export interface BondDetailsData {
  maturityYear: number;
  annualCouponPct: number;     // Decimal, e.g. 0.0425 for 4.25%
  nominalValue: number;        // Par/face value at redemption (GBP)
  issuerType: 'GILT' | 'CORPORATE';
}

export interface HoldingData {
  holdingId: string;
  accountType: 'SIPP' | 'ISA' | 'BROKERAGE';
  etfName: string;
  ticker?: string;
  assetClass: 'EQUITY' | 'BOND' | 'MIXED' | 'OTHER';
  currentValue: number;
  ocfPct?: number;  // Ongoing Charges Figure as decimal (e.g. 0.0022 for 0.22%)
  bondDetails?: BondDetailsData;  // Present only for individual bonds (not bond funds)
  /** BROKERAGE only — original purchase cost. Defaults to currentValue (0% gain) if omitted. */
  costBasis?: number;
  // Optional unit-quantity capture (#1531) — display/derivation only, never sent to the
  // engine. When both are present, currentValue is derived (quantity * unitPrice) and
  // kept live via the same auto-fill-then-editable pattern as OCF/ticker lookup.
  quantity?: number;
  unitPrice?: number;
  /** True once quantity tracking has ever been enabled for this holding — distinguishes
   * a genuine quantity change (transaction) from the first-time opt-in (0 -> N looks
   * identical to a purchase otherwise). See the BROKERAGE cost-basis prompt in rebaseline. */
  quantityTracked?: boolean;
}

export interface AdditionalPensionData {
  pensionId: string;
  name: string;
  annualAmount: number;          // today's money, before any early/late adjustment or commutation
  startYear: number;
  inflationLinked: boolean;      // legacy flag, kept in step with `indexation` (false only for NONE)
  // DB enrichment — mirrors server AdditionalPensionInput; optional numbers are omitted, never null
  indexation?: DbIndexation;
  fixedIndexationPct?: number;
  normalPensionAge?: number;
  earlyReductionPctPerYear?: number;
  lateIncreasePctPerYear?: number;
  automaticLumpSum?: number;
  commutedAnnual?: number;
  commutationFactor?: number;
  survivorPct?: number;          // JOINT plans only
  schemePreset?: string;         // display only
}

/** How an annuity's income rises each year after purchase. */
export type AnnuityEscalation = 'LEVEL' | 'FIXED' | 'RPI';

/**
 * A lifetime annuity bought from the pension inside the plan (Strategy step).
 * Stored per person as `annuityPurchases: { person0: [...], person1: [...] }`,
 * the same record shape as additionalPensions.
 */
export interface AnnuityPurchaseData {
  purchaseId: string;
  purchaseAge: number;
  amountMode: 'PERCENT' | 'FIXED';
  /** PERCENT mode: 1–100 of the pension value at purchase. */
  amountPct?: number;
  /** FIXED mode: today's money. */
  amountFixed?: number;
  takeTaxFreeCash: boolean;
  /** Income per £100 bought, e.g. 7.28. */
  annualRatePct: number;
  escalation: AnnuityEscalation;
  /** FIXED escalation only. */
  escalationPct?: number;
  jointLife: boolean;
  survivorPct?: number;
  /**
   * Wizard-only: true once the user has typed their own rate, which stops the
   * indicative rate re-filling on an age/type/joint change. The server schema
   * does not declare it, so Zod strips it before the projection request.
   */
  rateEdited?: boolean;
}

export interface ContributionPersonData {
  makingContributions: boolean;
  monthlyPersonalContribution: number;
  includeEmployer: boolean;
  monthlyEmployerContribution: number;
  annualIsaContribution: number;
  contributionEndDate: string;  // ISO date, pre-filled with retirement date
}

export interface Step3FormData {
  holdings: {
    [personIndex: string]: HoldingData[];
  };
  additionalPensions?: {
    [personIndex: string]: AdditionalPensionData[];
  };
  contributions?: {
    [personIndex: string]: ContributionPersonData;
  };
}

// Step 4: Property
export type PropertyType = 'PRIMARY_RESIDENCE' | 'BUY_TO_LET' | 'HOLIDAY_LET';
export type PropertyOwner = 'person0' | 'person1' | 'joint';

export interface PropertyItem {
  propertyId: string;
  label: string;
  propertyType: PropertyType;
  owner: PropertyOwner;
  currentMarketValue: number;
  currentMortgageAmount: number;
  currentEquity: number;
  planningSale: boolean;
  plannedSaleDate?: string;
  estateAgentFeePct: number;
  newPropertyPurchasePrice: number;
  proceedsStrategy: 'INVEST' | 'CASH';
  monthlyMortgagePayment: number;
  mortgageEndDate?: string;
  purchasePrice: number;
  /** Stamp duty, legal and survey fees paid on purchase; part of the CGT base cost. */
  acquisitionCosts: number;
  improvementCosts: number;
  /** Portion of currentMortgageAmount on interest-only terms (0 = fully repayment). The rest
   * (currentMortgageAmount - interestOnlyMortgageAmount) is the repayment portion — a "part and
   * part" mortgage is expressed as a value between 0 and currentMortgageAmount. */
  interestOnlyMortgageAmount: number;
  /** Annual interest rate as a percentage (e.g. 4.2 for 4.2%), applied to the whole mortgage. */
  mortgageInterestRatePct: number;
  legalFees: number;
  rental: {
    annualGrossIncome: number;
    annualAllowableExpenses: number;
    occupancyRatePct: number;
  } | null;
}

export interface Step4FormData {
  includeProperty: boolean;
  property?: {
    /** Read by StepTwilightYears for the "Sell <label>" copy. Declared here
     * because that read only compiled while step props were `Partial<any>`. */
    label?: string;
    currentMarketValue: number;
    currentMortgageAmount: number;
    currentEquity: number;
    planningSale: boolean;
    plannedSaleDate?: string;
    estateAgentFeePct: number;
    newPropertyPurchasePrice: number;
    proceedsStrategy?: 'INVEST' | 'CASH';
    monthlyMortgagePayment?: number;
    mortgageEndDate?: string;
  };
  properties?: PropertyItem[];
}

// Step 5: Supplementary Income
export interface SupplementaryIncomeItem {
  incomeId: string;       // crypto.randomUUID()
  personIndex: number;    // 0 or 1
  type: 'SALARY' | 'SELF_EMPLOYED' | 'CONSULTING' | 'OTHER';
  description: string;
  grossAnnualAmount: number;
  startYear: number;      // 1-based
  endYear?: number;       // undefined = indefinite
  inflationLinked: boolean;
}

export interface Step5SupplementaryIncomeFormData {
  supplementaryIncome: SupplementaryIncomeItem[];
}

// Care Scenario (within Step 6 - Spending)
export interface CareScenarioItem {
  scenarioId: string;            // crypto.randomUUID()
  personIndex: number;           // 0 or 1
  careType: 'HOME' | 'RESIDENTIAL' | 'NURSING';
  annualCost: number;            // Today's money
  startYear: number;             // 1-based
  durationYears: number;
  untilEndOfLife?: boolean;      // When true, durationYears is auto-calculated to deceasedAge or projection end
  inflationPremiumPct: number;   // e.g., 2 for 2% above CPI (UI %, converted to decimal in transform)
  sellPrimaryResidenceOnCareStart?: boolean;   // Trigger primary residence sale when care begins
  careSaleProceedsStrategy?: 'INVEST' | 'CASH'; // How to handle sale proceeds (default: CASH)
}

// Step 6 (was Step 5): Retirement Spending Plan

// Spending Phase

/** A single itemized expense within a spending-phase category (#1538). */
export interface ExpenseLineItem {
  itemId: string;      // UUID
  description: string;
  annualAmount: number; // Annual net £ in today's prices
}

export interface SpendingPhase {
  phaseId: string;  // UUID for tracking
  label: string;    // e.g., "Early Retirement - Active Years"
  startYear: number; // 1-based (Year 1 = retirement year)
  endYear?: number;   // Must be > startYear if set; blank = continues to projection end
  livingExpenses: number;   // Annual net £ in today's prices — always the transmitted total, even when itemized
  travelLeisure: number;    // Annual net £ in today's prices — always the transmitted total, even when itemized
  otherDiscretionary: number; // Annual net £ in today's prices — always the transmitted total, even when itemized
  // Optional itemized breakdown per category (#1538). When present, the flat
  // field above is kept in sync as the sum of these items by the UI — the
  // server/engine only ever reads the flat field, so old plans without these
  // fields render identically to today (see issue #1538 backward-compat notes).
  livingExpensesItems?: ExpenseLineItem[];
  travelLeisureItems?: ExpenseLineItem[];
  otherDiscretionaryItems?: ExpenseLineItem[];
  inflationLinked?: boolean; // Default: true — rises with inflation each year
}

// One-Time Expense
export interface OneTimeExpense {
  expenseId: string;  // UUID
  description: string;
  amount: number;     // Net £ in today's prices
  year: number;       // Year of expense (1-based)
  inflationLinked?: boolean; // Default: true for new items — rises with inflation
}

// Recurring Expense
export interface RecurringExpense {
  expenseId: string;  // UUID
  description: string;
  amountPerYear: number; // Net £ in today's prices
  startYear: number;
  endYear?: number;   // Optional: if omitted, runs to end of projection
  inflationLinked?: boolean; // Default: true for new items — rises with inflation
}

export interface Step6FormData {
  spendingPhases: SpendingPhase[]; // 1-5 phases (#1538) — was a fixed array of 3
  oneTimeExpenses: OneTimeExpense[];
  recurringExpenses: RecurringExpense[];
}

// Step 7 (Later Life & Estate): Deceased age, survivor spending, and care scenarios
export interface PersonDeceasedAge {
  personIndex: number;
  deceasedAge: number | null;
}

export interface IHTStrategiesFormData {
  annualGiftExemption: boolean;
  smallGiftRecipientsPerYear: number;
  charitableLegacyEnabled: boolean;
  normalExpenditureFromIncome: boolean;
  annualPetAmount: number;
  annualCharitableDonation: number;
}

export interface StepTwilightYearsFormData {
  hasDirectDescendants: boolean | null;
  includePensionsInEstate: boolean;
  careScenarios: CareScenarioItem[];
  personDeceasedAges: PersonDeceasedAge[];
  survivorSpendingPct: number;  // UI %, default 70
  ihtStrategies: IHTStrategiesFormData;
  /** Ring-fenced later-life care fund (£, today's money) — a floor on household cash until released. Omitted, never null. */
  careFund?: number;
  /** Age at which the care fund is released; released earlier if a care scenario starts. Omitted, never null. */
  careFundReleaseAge?: number;
}

// Step 7: Cash & Assumptions
export interface Step7FormData {
  householdCash: {
    jointAccountBalance: number;
  };
  economicAssumptions: {
    equityReturnAnnualPct: number;
    bondReturnAnnualPct: number;
    inflationAnnualPct: number;
    taxBandInflationPct: number;
    statePensionGrowthAddPct: number;
    dividendYieldPct: number;
  };
}

// Step 8: Withdrawal Strategy
export interface Step8FormData {
  decumulationStrategy: 'TAX_OPTIMISED' | 'ISA_FIRST' | 'SIPP_FIRST' | 'PROPORTIONAL';
  projectionYears: number;
  isaAnnualAllowance: number;
  isaShelteringMode: 'AEA_AWARE' | 'FULL';
  bandFillMode: 'PA_ONLY' | 'PA_AND_BASIC' | 'PA_AND_HIGHER';
  timeHorizonRebalancing?: boolean;
  /** Fixed-allocation equity override, 0–1 (bond = remainder), applied uniformly
   *  across wrappers. Absent = use the mix derived from holdings. Only meaningful
   *  when timeHorizonRebalancing is false. Deliberately NOT nullable — the server
   *  ProjectionInputSchema rejects an explicit null, so "no override" is omission. */
  allocationOverridePct?: number;
  /** Emergency fund to keep in cash (£, today's money) — a floor on household cash. Omitted, never null. */
  emergencyFund?: number;
  /** Lifetime annuities bought from the pension, keyed person0/person1. */
  annuityPurchases?: {
    [personIndex: string]: AnnuityPurchaseData[];
  };
}

// Combined form data
export type AllStepFormData = Step1FormData & Step2FormData & Step3FormData & Step4FormData & Step5SupplementaryIncomeFormData & Step6FormData & StepTwilightYearsFormData & Step7FormData & Step8FormData;

export interface WizardState {
  currentStep: number;
  maxStepReached: number; // Highest step user has visited
  formData: Partial<AllStepFormData>;
  planId?: string;
  lastSaved?: string;
  returnToReview?: boolean; // When editing from Review, Next returns to step 10
}

// Validation Schemas

/** YYYY-MM-DD format check — guards against locale-dependent new Date() parsing */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a YYYY-MM-DD string as a local-time Date, or return null if invalid */
export function parseLocalDate(dateStr: string): Date | null {
  if (!ISO_DATE_RE.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  // Verify the components survived (catches e.g. Feb 30)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

/** UK minimum pension age: 55 for born before 6 Apr 1971, 57 for born on/after */
export function getMinimumPensionAge(dateOfBirth: string): number {
  const dob = parseLocalDate(dateOfBirth);
  if (!dob) {
    throw new Error(`Invalid date of birth: "${dateOfBirth}"`);
  }
  const cutoffDate = new Date(1971, 3, 6); // 6 April 1971
  return dob < cutoffDate ? 55 : 57;
}

export const Step2Schema = z.object({
  persons: z.array(z.object({
    fullName: z.string().min(1, 'Full name is required'),
    dateOfBirth: z.string().min(1, 'Date of birth is required'),
    retirementDate: z.string().min(1, 'Planned retirement date is required'),
    statePensionAge: z.number().int().min(60, 'Must be between 60-70').max(70, 'Must be between 60-70'),
    currentStatePensionAnnual: z.number().min(0, 'Must be positive'),
    taxFreeLumpSumPct: z.number().min(0, 'Must be between 0-25').max(25, 'Must be between 0-25').step(0.01),
    taxFreeLumpSumCap: z.number().min(0, 'Must be positive'),
    lsaUsedPrePlan: z.number().min(0, 'Cannot be negative').default(0),
    priorOtherTaxableIncome: z.number().min(0, 'Cannot be negative').max(10_000_000, 'That looks too high — check for a typo').default(0),
    priorDividendIncome: z.number().min(0, 'Cannot be negative').max(10_000_000, 'That looks too high — check for a typo').default(0),
    priorSalaryIncome: z.number().min(0, 'Cannot be negative').max(10_000_000, 'That looks too high — check for a typo').default(0),
    priorDrawdownIncome: z.number().min(0, 'Cannot be negative').max(10_000_000, 'That looks too high — check for a typo').default(0),
    priorStatePensionIncome: z.number().min(0, 'Cannot be negative').max(10_000_000, 'That looks too high — check for a typo').default(0),
    priorRentalIncome: z.number().min(0, 'Cannot be negative').max(10_000_000, 'That looks too high — check for a typo').default(0),
    priorPensionContributions: z.number().min(0, 'Cannot be negative').max(10_000_000, 'That looks too high — check for a typo').default(0),
  })).min(1, 'At least one person required'),
}).superRefine((data, ctx) => {
  for (let i = 0; i < data.persons.length; i++) {
    const person = data.persons[i];
    if (!person.dateOfBirth || !person.retirementDate) continue;

    const dob = parseLocalDate(person.dateOfBirth);
    if (!dob) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['persons', i, 'dateOfBirth'],
        message: 'Date of birth is not a valid date (expected YYYY-MM-DD)',
      });
      continue;
    }

    const retirement = parseLocalDate(person.retirementDate);
    if (!retirement) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['persons', i, 'retirementDate'],
        message: 'Retirement date is not a valid date (expected YYYY-MM-DD)',
      });
      continue;
    }

    if (retirement <= dob) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['persons', i, 'retirementDate'],
        message: 'Retirement date must be after date of birth',
      });
      continue;
    }

    let retirementAge = retirement.getFullYear() - dob.getFullYear();
    const monthDiff = retirement.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && retirement.getDate() < dob.getDate())) {
      retirementAge--;
    }

    const minAge = getMinimumPensionAge(person.dateOfBirth);
    if (retirementAge < minAge) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['persons', i, 'retirementDate'],
        message: `Retirement age ${retirementAge} is below the UK minimum pension age of ${minAge} for this date of birth`,
      });
    }

    // Cross-field: lsaUsedPrePlan must not exceed taxFreeLumpSumCap
    if ((person.lsaUsedPrePlan ?? 0) > person.taxFreeLumpSumCap) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['persons', i, 'lsaUsedPrePlan'],
        message: 'Tax-free cash already used cannot exceed your total tax-free allowance',
      });
    }
  }
});

export const BondDetailsSchema = z.object({
  maturityYear: z.number().int().min(2025, 'Maturity year must be 2025 or later').max(2100),
  annualCouponPct: z.number().min(0, 'Coupon rate must be 0% or higher').max(0.20, 'Coupon rate must be 20% or lower'),
  nominalValue: z.number().positive('Face value must be greater than 0'),
  issuerType: z.enum(['GILT', 'CORPORATE']),
});

export const Step3Schema = z.object({
  holdings: z.record(
    z.array(z.object({
      holdingId: z.string(),
      accountType: z.enum(['SIPP', 'ISA', 'BROKERAGE']),
      etfName: z.string().min(1, 'Fund name is required'),
      ticker: z.string().optional(),
      assetClass: z.enum(['EQUITY', 'BOND', 'MIXED', 'OTHER']),
      currentValue: z.number().positive('Value must be greater than 0'),
      ocfPct: optionalNumber(z.number().min(0).max(0.1)),
      bondDetails: BondDetailsSchema.optional(),
    }))
  ),
});

const RentalConfigSchema = z.object({
  annualGrossIncome: z.number().min(0),
  annualAllowableExpenses: z.number().min(0),
  occupancyRatePct: z.number().min(0).max(100),
});

export const PropertyItemSchema = z.object({
  propertyId: z.string().min(1),
  label: z.string().min(1).max(100),
  propertyType: z.enum(['PRIMARY_RESIDENCE', 'BUY_TO_LET', 'HOLIDAY_LET']),
  owner: z.enum(['person0', 'person1', 'joint']),
  currentMarketValue: z.number(),
  currentMortgageAmount: z.number(),
  currentEquity: z.number(),
  planningSale: z.boolean(),
  plannedSaleDate: z.string().optional(),
  estateAgentFeePct: z.number().min(0, 'Must be between 0-10').max(10, 'Must be between 0-10').step(0.01),
  newPropertyPurchasePrice: z.number().min(0, 'Must be 0 or greater'),
  proceedsStrategy: z.enum(['INVEST', 'CASH']).default('INVEST'),
  monthlyMortgagePayment: z.number().min(0, 'Must be 0 or greater'),
  mortgageEndDate: z.string().optional(),
  purchasePrice: z.number().min(0),
  // An empty number input is NaN, not undefined, so a plain default never
  // fires and the step would refuse to continue with no message. Blank means 0.
  acquisitionCosts: z.preprocess(
    (v) => (v === '' || v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v)) ? 0 : v),
    z.number().min(0, 'Must be 0 or greater'),
  ),
  improvementCosts: z.number().min(0),
  interestOnlyMortgageAmount: z.number().min(0).default(0),
  mortgageInterestRatePct: z.number().min(0).max(20, 'Must be between 0-20').default(0),
  legalFees: z.number().min(0),
  rental: RentalConfigSchema.nullable(),
}).superRefine((data, ctx) => {
  if (data.interestOnlyMortgageAmount > data.currentMortgageAmount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Interest-only portion cannot exceed the outstanding mortgage',
      path: ['interestOnlyMortgageAmount'],
    });
  }
});

export const Step4Schema = z.object({
  includeProperty: z.boolean(),
  property: z.object({
    currentMarketValue: z.number(),
    currentMortgageAmount: z.number(),
    currentEquity: z.number(),
    planningSale: z.boolean(),
    plannedSaleDate: z.string().optional(),
    estateAgentFeePct: z.number().min(0, 'Must be between 0-10').max(10, 'Must be between 0-10').step(0.01),
    newPropertyPurchasePrice: z.number().min(0, 'Must be 0 or greater'),
    proceedsStrategy: z.enum(['INVEST', 'CASH']).default('INVEST'),
    monthlyMortgagePayment: optionalNumber(z.number().min(0, 'Must be 0 or greater')),
    mortgageEndDate: z.string().optional(),
  }).optional(),
  properties: z.array(PropertyItemSchema).optional(),
}).superRefine((data, ctx) => {
  if (!data.includeProperty) return;

  // Validate multi-property array if present
  if (data.properties && data.properties.length > 0) {
    let primaryCount = 0;
    for (let i = 0; i < data.properties.length; i++) {
      const prop = data.properties[i];
      if (prop.propertyType === 'PRIMARY_RESIDENCE') primaryCount++;

      if (prop.currentMarketValue <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Market value must be greater than 0',
          path: ['properties', i, 'currentMarketValue'],
        });
      }
      if (prop.currentEquity <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Equity must be greater than 0',
          path: ['properties', i, 'currentEquity'],
        });
      }
      const sum = prop.currentMortgageAmount + prop.currentEquity;
      if (Math.abs(sum - prop.currentMarketValue) >= 0.01) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Mortgage + equity must equal market value',
          path: ['properties', i, 'currentEquity'],
        });
      }
    }
    if (primaryCount > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At most one property can be your primary residence',
        path: ['properties'],
      });
    }
    return;
  }

  // Legacy single property validation
  if (!data.property) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Property details are required',
      path: ['property'],
    });
    return;
  }

  if (data.property.currentMarketValue <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Market value must be greater than 0',
      path: ['property', 'currentMarketValue'],
    });
  }

  if (data.property.currentMortgageAmount < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Mortgage cannot be negative',
      path: ['property', 'currentMortgageAmount'],
    });
  }

  if (data.property.currentEquity <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Equity must be greater than 0. This tool does not support negative equity scenarios.',
      path: ['property', 'currentEquity'],
    });
  }

  const sum = data.property.currentMortgageAmount + data.property.currentEquity;
  if (Math.abs(sum - data.property.currentMarketValue) >= 0.01) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Mortgage + equity must equal market value',
      path: ['property', 'currentEquity'],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Mortgage + equity must equal market value',
      path: ['property', 'currentMortgageAmount'],
    });
  }
});

// Step 5 Schema - Supplementary Income
const SupplementaryIncomeItemSchema = z.object({
  incomeId: z.string(),
  personIndex: z.number().int().min(0).max(1),
  type: z.enum(['SALARY', 'SELF_EMPLOYED', 'CONSULTING', 'OTHER']),
  description: z.string().min(1, 'Description is required'),
  grossAnnualAmount: z.number().positive('Amount must be greater than 0'),
  startYear: z.number().int().min(1),
  endYear: optionalNumber(z.number().int().min(1)),
  inflationLinked: z.boolean(),
}).refine(
  data => !data.endYear || data.endYear >= data.startYear,
  { message: 'End year must be after or equal to start year', path: ['endYear'] }
);

export const Step5Schema = z.object({
  supplementaryIncome: z.array(SupplementaryIncomeItemSchema),
});

// Step 6 Schema - Spending phases with complex validation

export const ExpenseLineItemSchema = z.object({
  itemId: z.string().uuid(),
  description: z.string().min(1, 'Description is required').max(100),
  annualAmount: z.number().min(0, 'Must be 0 or greater'),
});

const SpendingPhaseBaseFields = {
  phaseId: z.string().uuid(),
  label: z.string().min(1, 'Phase label is required').max(100),
  startYear: z.number().int().min(1),
  livingExpenses: z.number().min(0, 'Must be 0 or greater'),
  travelLeisure: z.number().min(0, 'Must be 0 or greater'),
  otherDiscretionary: z.number().min(0, 'Must be 0 or greater'),
  livingExpensesItems: z.array(ExpenseLineItemSchema).optional(),
  travelLeisureItems: z.array(ExpenseLineItemSchema).optional(),
  otherDiscretionaryItems: z.array(ExpenseLineItemSchema).optional(),
  inflationLinked: z.boolean().optional(),
};

// endYear is optional on every element — only the LAST phase in the array may
// actually leave it blank (continues to end of projection / life expectancy);
// that positional rule is enforced by Step6Schema's superRefine below, since
// a per-element schema can't see the element's position in the array.
const SpendingPhaseSchema = z.object({
  ...SpendingPhaseBaseFields,
  endYear: optionalNumber(z.number().int().min(1)),
}).refine(
  data => !data.endYear || data.endYear > data.startYear,
  { message: 'End year must be after start year', path: ['endYear'] }
);

const OneTimeExpenseSchema = z.object({
  expenseId: z.string().uuid(),
  description: z.string().min(1, 'Description is required'),
  amount: z.number().positive('Amount must be greater than 0'),
  year: z.number().int().min(1),
  inflationLinked: z.boolean().optional(),
});

const RecurringExpenseSchema = z.object({
  expenseId: z.string().uuid(),
  description: z.string().min(1, 'Description is required'),
  amountPerYear: z.number().positive('Amount must be greater than 0'),
  startYear: z.number().int().min(1),
  endYear: optionalNumber(z.number().int().min(1)),
  inflationLinked: z.boolean().optional(),
}).refine(
  data => !data.endYear || data.endYear >= data.startYear,
  { message: 'End year must be after or equal to start year', path: ['endYear'] }
);

const CareScenarioItemSchema = z.object({
  scenarioId: z.string(),
  personIndex: z.number().int().min(0).max(1),
  careType: z.enum(['HOME', 'RESIDENTIAL', 'NURSING']),
  annualCost: z.number().positive('Cost must be greater than 0').max(500_000),
  startYear: z.number().int().min(1),
  durationYears: z.number().int().min(1).max(30),
  untilEndOfLife: z.boolean().optional().default(false),
  inflationPremiumPct: z.number().min(0).max(10),
  sellPrimaryResidenceOnCareStart: z.boolean().optional().default(false),
  careSaleProceedsStrategy: z.enum(['INVEST', 'CASH']).optional().default('CASH'),
});

export const Step6Schema = z.object({
  spendingPhases: z.array(SpendingPhaseSchema)
    .min(1, 'At least one spending phase is required')
    .max(5, 'A plan can have at most 5 spending phases'),
  oneTimeExpenses: z.array(OneTimeExpenseSchema),
  recurringExpenses: z.array(RecurringExpenseSchema),
}).superRefine((data, ctx) => {
  const phases = data.spendingPhases;
  const first = phases[0];

  if (first && first.startYear !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'First phase must start at Year 1',
      path: ['spendingPhases', 0, 'startYear'],
    });
  }

  // Every phase except the last must have an end year that hands off exactly
  // to the next phase's start year. Only the last phase may be open-ended
  // (continues to end of projection / life expectancy).
  for (let i = 0; i < phases.length - 1; i++) {
    const phase = phases[i];
    const next = phases[i + 1];

    if (phase.endYear == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Phase ${i + 1} must have an end year — only the final phase can be open-ended`,
        path: ['spendingPhases', i, 'endYear'],
      });
      continue;
    }

    if (next.startYear !== phase.endYear + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Phase ${i + 1} end year must be immediately before Phase ${i + 2} start`,
        path: ['spendingPhases', i, 'endYear'],
      });
    }
  }

  // If the final phase has an end year, it must be after its start year (already checked by schema refine).
  // No cross-phase check needed for the final phase — it simply continues to projection end if blank.
});

// Step 7 (Later Life & Estate) Schema
const PersonDeceasedAgeSchema = z.object({
  personIndex: z.number().int().min(0).max(1),
  deceasedAge: z.number().int().min(1).max(120).nullable(),
});

const IHTStrategiesSchema = z.object({
  annualGiftExemption: z.boolean(),
  smallGiftRecipientsPerYear: z.number().int().min(0).max(50),
  charitableLegacyEnabled: z.boolean(),
  normalExpenditureFromIncome: z.boolean(),
  annualPetAmount: z.number().min(0),
  annualCharitableDonation: z.number().min(0),
});

export const StepTwilightYearsSchema = z.object({
  hasDirectDescendants: z.boolean().nullable().default(null),
  includePensionsInEstate: z.boolean().default(true),
  careScenarios: z.array(CareScenarioItemSchema),
  personDeceasedAges: z.array(PersonDeceasedAgeSchema),
  survivorSpendingPct: z.number().min(1).max(100),
  ihtStrategies: IHTStrategiesSchema,
  // Ring-fenced care fund: both optional, empty saves as omission (never null)
  careFund: optionalNumber(z.number().min(0, 'Must be 0 or greater')),
  careFundReleaseAge: optionalNumber(z.number().int('Must be a whole number').min(50, 'Must be between 50 and 120').max(120, 'Must be between 50 and 120')),
}).superRefine((data, ctx) => {
  if (data.careFundReleaseAge != null && !(data.careFund != null && data.careFund > 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['careFundReleaseAge'],
      message: 'A release age needs a care fund amount above zero',
    });
  }
});

export const Step7Schema = z.object({
  householdCash: z.object({
    jointAccountBalance: z.number().min(0, 'Must be 0 or greater'),
  }),
  economicAssumptions: z.object({
    equityReturnAnnualPct: z.number().min(-5, 'Must be between -5% and 20%').max(20, 'Must be between -5% and 20%').step(0.01),
    bondReturnAnnualPct: z.number().min(-5, 'Must be between -5% and 20%').max(20, 'Must be between -5% and 20%').step(0.01),
    inflationAnnualPct: z.number().min(0, 'Must be between 0% and 15%').max(15, 'Must be between 0% and 15%').step(0.01),
    taxBandInflationPct: z.number().min(0, 'Must be between 0% and 100%').max(100, 'Must be between 0% and 100%').step(1),
    statePensionGrowthAddPct: z.number().min(0, 'Must be between 0% and 5%').max(5, 'Must be between 0% and 5%').step(0.01),
    dividendYieldPct: z.number().min(0, 'Must be between 0% and 10%').max(10, 'Must be between 0% and 10%').step(0.01),
  }),
});

export const Step8Schema = z.object({
  decumulationStrategy: z.enum(['TAX_OPTIMISED', 'ISA_FIRST', 'SIPP_FIRST', 'PROPORTIONAL']),
  projectionYears: z.number().int().min(10, 'Must be between 10-100').max(100, 'Must be between 10-100'),
  isaAnnualAllowance: z.number().int().min(0).max(20000, 'ISA allowance cannot exceed £20,000 per person'),
  isaShelteringMode: z.enum(['AEA_AWARE', 'FULL']),
  bandFillMode: z.enum(['PA_ONLY', 'PA_AND_BASIC', 'PA_AND_HIGHER']),
  timeHorizonRebalancing: z.boolean().optional().default(false),
  // Equity fraction 0–1 (bond = remainder), applied uniformly in Fixed mode.
  // "Reset to my holdings' mix" clears it to undefined (omission), NOT null —
  // the server projection schema rejects an explicit null.
  allocationOverridePct: z.number().min(0).max(1).optional(),
  // Emergency fund floor on household cash; empty saves as omission (never null)
  emergencyFund: optionalNumber(z.number().min(0, 'Must be 0 or greater')),
  // Lifetime annuities per person. Optional numbers are omitted, never null —
  // the server projection schema rejects an explicit null. Cross-field rules
  // (purchase age vs minimum pension age and horizon, joint life on JOINT
  // plans) are enforced server-side in validateAnnuityPurchases.
  annuityPurchases: z.record(z.enum(['person0', 'person1']), z.array(z.object({
    purchaseId: z.string().min(1),
    purchaseAge: z.number().int().min(55, 'Must be 55 or older').max(100, 'Must be 100 or younger'),
    amountMode: z.enum(['PERCENT', 'FIXED']),
    amountPct: z.number().min(1, 'Must be between 1% and 100%').max(100, 'Must be between 1% and 100%').optional(),
    amountFixed: z.number().positive('Enter an amount').max(10_000_000, 'Amount cannot exceed £10M').optional(),
    takeTaxFreeCash: z.boolean(),
    annualRatePct: z.number().min(0.5, 'Rate must be between 0.5 and 20').max(20, 'Rate must be between 0.5 and 20'),
    escalation: z.enum(['LEVEL', 'FIXED', 'RPI']),
    escalationPct: z.number().min(0).max(10).optional(),
    jointLife: z.boolean(),
    survivorPct: z.number().min(0).max(100).optional(),
    rateEdited: z.boolean().optional(),
  }).refine(
    a => a.amountMode !== 'PERCENT' || a.amountPct != null,
    { message: 'Enter the percentage of the pension to use', path: ['amountPct'] },
  ).refine(
    a => a.amountMode !== 'FIXED' || a.amountFixed != null,
    { message: 'Enter the amount of pension to use', path: ['amountFixed'] },
  ))).optional(),
});
